/* eslint-disable no-param-reassign */
import { libraryApi } from "@iventis/api/src/api";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { findNode, findNodesByType, recursivelyAppendTopSelection } from "@iventis/tree-browser";
import qs from "qs";
import { canRead } from "@iventis/utilities";
import { convertNodeTreeDates } from "./services/library-api-functions";
import { importLayers } from "./services/layer-service";
import { orderImportedLayerNodes } from "./sidebar-order-helpers";
import { getMapLayers, getMapNodeFromLayerId } from "./import-layer-api-functions";

/**
 * Searches a node to select the ones which are not already in the map
 * @param node - Node to search
 * @param alreadyInMapLayerIds - List of layer ids already in the map
 * @param alreadyImportedNodeIds - List of layer ids which are already been process
 * @returns - A node with all the children which are not already in the map
 */
export function getNodesWhichAreNotInMap(node: Node, alreadyInMapLayerIds: string[], alreadyImportedNodeIds: string[]) {
    node.childNodes = recursiveNodeCheck(node.childNodes, alreadyInMapLayerIds, alreadyImportedNodeIds);
    return node;
}

function recursiveNodeCheck(nodes: Node[], alreadyInMapLayerIds: string[], alreadyImportedNodeIds: string[]) {
    const nodesAllowed: Node[] = [];
    nodes.forEach((node) => {
        // Add the layer node if it has not been added already
        if (node.type === NodeType.Layer && canNodeBeAdded(node, alreadyInMapLayerIds, alreadyImportedNodeIds)) {
            nodesAllowed.push(node);
            alreadyImportedNodeIds.push(node.sourceId);
        }

        // If not a layer node, check its children
        if (node.type !== NodeType.Layer) {
            const childNodes = recursiveNodeCheck(node.childNodes, alreadyInMapLayerIds, alreadyImportedNodeIds);
            node.childNodes = childNodes;
            // If the node has valid children, add it to the list
            if (childNodes.length > 0) {
                nodesAllowed.push(node);
                alreadyImportedNodeIds.push(node.sourceId);
            }
        }
    });
    return nodesAllowed;
}

const canNodeBeAdded = (node: Node, alreadyInMapLayerIds: string[], alreadyImportedNodeIds: string[]) =>
    !alreadyInMapLayerIds.includes(node.sourceId) && !alreadyImportedNodeIds.includes(node.sourceId);

/**
 * Removes all nodes which are not valid for import, these are folders outside of maps and maps
 */
export function filterNonValidMapChildren(importedNodes: Node[]): Node[] {
    const validNodes: Node[] = [];

    importedNodes.forEach((node) => {
        const mapNodes = findNodesByType([node], NodeType.Map);

        // If there are no map nodes we assume all the nodes are from within a map
        if (mapNodes.length === 0) {
            validNodes.push(node);
        } else {
            // If there are map nodes, we only want to import the children of the map nodes
            mapNodes.forEach((mapNode) => {
                validNodes.push(...mapNode.childNodes);
            });
        }
    });

    return validNodes;
}

/**
 * Import the selected layers and copy the original folder structure
 *
 * Bug reference: 15234 - Slow and error when importing and unlinking lots of layers
 * @param mappingLibrary The entire tree for the mapping library the user has open when selecting the layers to import
 * @param selectedNodeIds The selected node ids
 * @param currentMap The current map id and the source ids of its' layers
 * @returns
 */
export const importLayersAndFolders = async (
    mappingLibrary: Node,
    selectedNodeIds: string[],
    currentMap: { mapId: string; originLayerIdsAlreadyInMap: string[] },
    importId: string
) => {
    // Get the top selected nodes
    const selection = recursivelyAppendTopSelection(mappingLibrary, selectedNodeIds);

    const importTree: Node[] = [];
    const uniqueIds: string[] = [];
    const grouplessLayerImports: Record<string, boolean> = {};

    await Promise.all(
        selection.map(async (node) => {
            // If the node is already in the map, don't import it.
            // If a node of the same source has already been added to this import, don't import it.
            if (uniqueIds.includes(node.sourceId) || currentMap.originLayerIdsAlreadyInMap.includes(node.sourceId)) {
                return;
            }
            if (node.type === NodeType.Folder || node.type === NodeType.Map) {
                const { data: nodeWithChildren } = await libraryApi.get<Node>(`nodes/${node.id}/children`, {
                    params: {
                        expandNodeType: [NodeType.PersonalMappingLibrary, NodeType.MappingLibrary, NodeType.Folder, NodeType.Map],
                    },
                    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                });

                convertNodeTreeDates(nodeWithChildren);

                // Remove all nodes which have already been imported or are going to be imported to the map
                const nodeToImport = getNodesWhichAreNotInMap(nodeWithChildren, currentMap.originLayerIdsAlreadyInMap, uniqueIds);

                importTree.push(...(nodeToImport.type === NodeType.Map ? nodeToImport.childNodes : [nodeToImport]));
            } else if (node.type === NodeType.Layer) {
                uniqueIds.push(node.sourceId);
                importTree.push(node);
                grouplessLayerImports[node.sourceId] = true;
            }
        })
    );

    const layerNodesToImport = findNodesByType(importTree, NodeType.Layer).filter((node) => canRead(node.authorisation));

    if (layerNodesToImport.length === 0) {
        // If we have no layers to import, abort
        return [];
    }

    // For each layer being imported, get it's map node and group by map
    const mapsWithImportedNodes = await getMapNodesFromLayerNodes(layerNodesToImport);

    // For each map being imported from, get all of it's layers
    const mapsWithImportedNodesAndAllLayers = await Promise.all(
        mapsWithImportedNodes.map(async (map) => {
            const layers = await getMapLayers(map.mapNode.id);
            return { ...map, layers };
        })
    );

    // Ensure layers appear in the sidebar in the same order as their original plan.
    // When importing from multiple plans, layers are grouped by plan and ordered according to their order within each plan.
    // For example - Plan A layers, then Plan B, then Plan C, etc.
    const orderedLayerNodesToImport = mapsWithImportedNodesAndAllLayers
        .sort((a, b) => a.mapNode.name.localeCompare(b.mapNode.name))
        .flatMap(({ mapNode, layerNodesToImport, layers }) => orderImportedLayerNodes(mapNode, layerNodesToImport, layers));

    const importName = getImportLayersName(orderedLayerNodesToImport);

    const mapNodes = findNodesByType([...importTree, ...mappingLibrary.childNodes], NodeType.Map);

    const layerImportRequests = orderedLayerNodesToImport.map((node) => ({
        mapLayerId: node.id,
        // If the layers' parent is a map or if the group isn't being imported, we don't need to specify the originMapLayerGroupId
        mapLayerGroupId: mapNodes?.some((mapNode) => [mapNode.id, mapNode.sourceId].includes(node.parentId)) || grouplessLayerImports[node.sourceId] ? undefined : node.parentId,
    }));

    // Request the import - no need to await it
    importLayers(currentMap.mapId, {
        id: importId,
        layerImportRequests,
        name: importName,
    });

    return layerNodesToImport;
};

export const getImportLayersName = (layerNodesToImport: Node[]) => (layerNodesToImport.length === 1 ? layerNodesToImport[0].name : `${layerNodesToImport[0].name}, ...`);

/** Given an array of layer nodes, return the map node (with all it's children) for each one */
export async function getMapNodesFromLayerNodes(layerNodes: Node[]) {
    const mapsWithImportedNodes: { layerNodesToImport: Node[]; mapNode: Node }[] = [];
    const alreadyFetchedMapNodes: Node[] = [];

    for (const layerNode of layerNodes) {
        // Check for the node we are checking, if we have already fetched it's map node.
        let mapNode = alreadyFetchedMapNodes.find((mapNode) => findNode(mapNode.childNodes, layerNode.id));
        // If we haven't fetched it then get it
        if (mapNode == null) {
            mapNode = await getMapNodeFromLayerId(layerNode.id);
            alreadyFetchedMapNodes.push(mapNode);
        }

        // Once we have fetched it, add the layer node to the other layer nodes belonging to that plan
        const foundMapNodeWithImportedNodes = mapsWithImportedNodes.find((mapWithImportedNode) => mapWithImportedNode.mapNode.id === mapNode.id);
        if (foundMapNodeWithImportedNodes == null) {
            mapsWithImportedNodes.push({ mapNode, layerNodesToImport: [layerNode] });
        } else {
            foundMapNodeWithImportedNodes.layerNodesToImport.push(layerNode);
        }
    }

    return mapsWithImportedNodes;
}
